<script setup lang="ts">
import { computed } from "@vue/reactivity";
import { getCurrentInstance, PropType, ref, watch, onMounted, nextTick } from "vue";
import { PropLike, usePermission } from "../../../composables/permission";
import { listen } from "../../../utils/bus";
import { isObject, isPromise, useOnOutsidePress } from "vue-composable";
import { openModal, openSlideover } from "../../../composables/dialog/drawer";
import SimpleMenu from "../inputs/SearchRenderers/SimpleMenu.vue";
import { Key } from "ts-key-enum";
import { useRouter } from "vue-router";
import { randomID } from "../../../utils/helpers";

//// PROPS
const props = withDefaults(
  defineProps<{
    label: string;
    ghost?: boolean;
    border?: boolean;
    outline?: boolean;
    click?: Function;
    type?: string;
    color?: string;
    disabled?: boolean;
    menu?: boolean;
    icon?: string;
    iconOnly?: boolean;
    options?: PropType<Array<{ label: string }> | Record<string, { label: string }>>;
  }>(),
  {},
);

//// LOCAL VARS
const isOpened = ref(false);
const focusEl = ref<HTMLElement | null>(null);
const isFocus = ref(false);
const submitting = ref(false);
const { proxy } = getCurrentInstance()!;
const optionAccess = ref([]);
const router = useRouter();
const targetId = randomID("actions-menu");
const itemRefs = ref([]);
//// COMPUTED VARS
const items = computed(() => {
  const options = props.options;
  if (!options) return [];
  if (Array.isArray(options)) {
    return [
      {
        name: "",
        items: options,
      },
    ];
  }
  return Object.keys(options).map((x) => ({
    name: x,
    items: options[x],
  }));
});

// Fix for sentry bug STAFF-APP-4B5
const hasMenuItems = () => itemRefs.value.length > 0;

onMounted(() => {
  nextTick(() => {
    // Set initial tabindex
    if (hasMenuItems()) {
      itemRefs.value.forEach((el, i) => {
        if (el) el.tabIndex = i === 0 ? 0 : -1;
      });
    }
  });
});

//// FUNCTIONS

function handleKey() {
  isFocus.value = true;
  isOpened.value = !isOpened.value;

  if (isOpened.value) {
    focusFirstMenuItem();
  }
}

const handleClick = () => {
  isOpened.value = !isOpened.value;
  isFocus.value = !isFocus.value;
  if (isOpened.value) {
    focusFirstMenuItem();
  }
};

function handleBlur() {
  isFocus.value = false;
}

const closeMenu = () => {
  isOpened.value = false;
  focusEl.value?.focus();
};

const focusFirstMenuItem = () => {
  nextTick(() => {
    if (hasMenuItems()) {
      itemRefs.value[0].focus();
    }
  });
};

async function itemClick(item: PropLike, ev: MouseEvent | KeyboardEvent) {
  if (item.disabled) return;
  if (isDisabled(item)) return;
  if (ev instanceof KeyboardEvent) {
    if (ev.code === Key.Escape) closeMenu();
    if (ev.code !== Key.Enter) return;
  }
  const click = (item.click || item.onClick) as (e: MouseEvent | KeyboardEvent) => void;
  focusEl.value?.focus();
  if (click) {
    const res = click(ev);
    if (isPromise(res)) {
      submitting.value = true;
      try {
        await res;
      } finally {
        submitting.value = false;
      }
      closeMenu();
      return;
    }
    closeMenu();
    return;
  }

  if (item.toModal) {
    const [url, options] = isObject(item.toModal)
      ? [item.toModal.url, item.toModal]
      : [item.toModal, {}];

    if (options?.stopEventPropagation === true) {
      ev.stopPropagation();
    }
    closeMenu();
    return openModal(url, options);
  }

  if (item.toSlideover) {
    const [url, options] = isObject(item.toSlideover)
      ? [item.toSlideover.url, item.toSlideover]
      : [item.toSlideover, {}];

    if (options?.stopEventPropagation === true) {
      ev.stopPropagation();
    }
    closeMenu();
    return openSlideover(url, options);
  }

  if (ev instanceof MouseEvent) {
    closeMenu();
  }
}

useOnOutsidePress(
  computed(() => proxy.$el),
  () => {
    if (isOpened.value) {
      closeMenu();
      handleBlur();
    }
  },
);

function isDisabled(item: any) {
  return optionAccess.value[item.label]?.disabled;
}

const onKeydown = (item: PropLike, ev: MouseEvent | KeyboardEvent) => {
  const items = itemRefs.value.filter(Boolean);

  const currentIndex = items.findIndex((el) => el === document.activeElement);
  if (currentIndex === -1) return;

  let nextIndex = currentIndex;

  if (ev.key === "ArrowDown") {
    ev.preventDefault();
    nextIndex = (currentIndex + 1) % items.length;
  } else if (ev.key === "ArrowUp") {
    ev.preventDefault();
    nextIndex = (currentIndex - 1 + items.length) % items.length;
  } else if (ev.key === "Enter") {
    itemClick(item, ev);
  } else if (ev.key === "Escape" || ev.key === "Tab") {
    closeMenu();
    return;
  }

  if (nextIndex !== currentIndex) {
    items.forEach((el, i) => (el.tabIndex = i === nextIndex ? 0 : -1));
    items[nextIndex].focus();
  }
};

//// WATCHERS & LISTENERS
listen("close-menus", (data: any) => {
  if (data.target === targetId || data === true) closeMenu();
});

watch(isOpened, (v) => {
  if (v && items.value.length > 0) {
    for (let option of items.value) {
      if (Array.isArray(option.items)) {
        for (const o of option.items) {
          const { disabled } = usePermission(o, true, router);
          optionAccess.value[o.label] = { o, disabled };
        }
      }
    }
  }
});
</script>
<template>
  <div ref="focusEl" class="m-dropdown-button-new">
    <div
      :id="`button-${targetId}`"
      ref="buttonEl"
      class="button"
      :class="[
        { disabled: disabled },
        'medicus-outline',
        color || type,
        { 'menu-style': menu, ghost: ghost || menu, raised: !border && !ghost && !menu, outline },
      ]"
      tabindex="0"
      role="button"
      aria-haspopup="menu"
      :aria-expanded="isOpened"
      aria-label="Actions menu"
      @click="handleClick"
      @keydown.enter="handleKey"
      @keydown.esc="closeMenu"
      @blur="handleBlur"
    >
      <span class="button-label">{{ label }}</span>
      <div class="select-caret-icon" aria-hidden="true">
        <i v-if="!isOpened" class="fa fa-caret-down" />
        <i v-else class="fa fa-caret-up" />
      </div>
    </div>
    <template v-if="$slots.label || iconOnly">
      <slot name="label">
        <span class="sr-only">{{ label }}</span>
      </slot>
    </template>

    <SimpleMenu :id="targetId" no-auto-open :model-value="isOpened" :block-dispatch="true">
      <div
        :class="{ 'grouped-list': !Array.isArray(options) }"
        role="menu"
        :aria-labelledby="`button-${targetId}`"
      >
        <div
          v-for="(group, index) in items"
          :key="`option-${index}`"
          class="dropdown-group"
          role="group"
        >
          <div v-if="group.name" class="dropdown-item no-hover dropdown-button-list-title">
            {{ group.name }}
          </div>
          <div
            v-for="(option, i) in group.items"
            :key="`option-item-${i}`"
            v-bind="option"
            ref="itemRefs"
            class="dropdown-item"
            :class="{
              disabled: isDisabled(option),
            }"
            :tabindex="index === 0 && i === 0 ? 0 : -1"
            clickable
            role="menuitem"
            @click="(ev) => itemClick(option, ev)"
            @keydown="(ev) => onKeydown(option, ev)"
          >
            <div class="dropdown-button-list-label">
              {{ option.label }}
              <div v-if="isDisabled(option)" class="dropdown-button-list-tooltip">
                You don’t have permission to perform this action.
              </div>
            </div>
          </div>
        </div>
      </div>
    </SimpleMenu>
  </div>
</template>

<style lang="scss">
.m-dropdown-button-new {
  // text
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 150%;
  height: 35px;
  /* identical to box height */
  text-align: left;
  border-radius: 4px;
  border: 1px solid var(--theme-accent-blue);
  background: var(--theme-blue);
  color: #ffffff;
  cursor: pointer;
  outline-color: var(--theme-blue);
  outline-width: 20px;
  outline: none;

  user-select: none;

  display: flex;
  align-items: center;
  .button {
    padding: 2px 12px;
  }
  .dropdown-item {
    min-height: 30px !important;
    border-radius: 4px;
    padding: 4px 10px 4px 16px;
    font-size: 14px;
    font-weight: 400;
    font-style: normal;

    &:hover,
    &:focus {
      background-color: var(--grey-light);
    }
    &.no-hover {
      background-color: #ffffff !important;
    }
    &.disabled {
      // need to override so that the tooltip hover covers the behind content
      opacity: 1 !important;

      .dropdown-button-list-label {
        color: var(--text-color-lightest) !important;
      }
    }
  }

  .dropdown-button-list-title {
    color: var(--text-color-light);
    cursor: default;
  }

  .dropdown-button-list-label {
    color: var(--text-color);

    .dropdown-button-list-tooltip {
      visibility: hidden;
      background-color: var(--grey-darkest);
      font-size: 10px;
      color: #fff;
      text-align: center;
      padding: 3px 6px;
      margin-top: 5px;
      border-radius: 6px;

      /* Position the tooltip text - see examples below! */
      position: absolute;
      z-index: 1;
    }

    &:hover .dropdown-button-list-tooltip {
      visibility: visible;
    }
  }

  .button {
    display: flex;
    direction: ltr;
  }

  .button-label {
    text-align: center;
  }
}
</style>
